import { useState, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { ContextStrip } from './ContextStrip'
import { ActionStrip } from './ActionStrip'
import { FilterBar } from './FilterBar'
import { SelectionBar } from './SelectionBar'
import { TransactionList } from './TransactionList'
import { UploadZone } from './UploadZone'
import { UploadProgressStrip } from './UploadProgressStrip'
import { useUploadTracker } from './hooks/useUploadTracker'
import { useReconParams } from './hooks/useReconParams'
import { useReconStore } from './hooks/useReconStore'
import { useTransactions, useClientFiles, useBankAccounts, useMatches } from './hooks/useReconApi'
import { Building2 } from 'lucide-react'

export function ReconciliationV2Page() {
  const {
    params,
    setClient,
    setAccount,
    setAccountKeepStatement,
    setStatement,
    setFilter,
    setDateRange,
    setSearch,
  } = useReconParams()
  const { expandedRows, toggleRow, selectedIds, toggleSelect, setSelected, clearSelection } = useReconStore()
  const focusHandledRef = useRef<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const queryClient = useQueryClient()

  const hasAccount = params.accountId != null
  const hasStatement = !!params.statementId

  const { data: files = [], isLoading: filesLoading } = useClientFiles(params.clientId)
  const { data: bankAccounts = [] } = useBankAccounts(params.clientId)

  const bankAccountLabel = useMemo(() => {
    const acc = bankAccounts.find((a) => a.id === params.accountId)
    if (!acc) return ''
    return `${acc.code}${acc.sub_code ? `.${acc.sub_code}` : ''} ${acc.name}`
  }, [bankAccounts, params.accountId])

  const { data: rawTransactions = [], isLoading: txnLoading } = useTransactions(
    hasStatement ? params.clientId : null,
    params.statementId
  )

  const { data: matches = [] } = useMatches(
    hasStatement ? params.clientId : null
  )

  const transactions = useMemo(() => {
    if (matches.length === 0) return rawTransactions
    // A transaction can have multiple match records (e.g. an old reversed match
    // plus a new active one). Prefer the active (non-terminal) match; if none,
    // fall back to the most recent. Backend returns matches in created_at DESC
    // order, so the first occurrence per txn is the newest.
    const matchByTxn = new Map<string, typeof matches[number]>()
    for (const m of matches) {
      const isTerminal = m.state === 'reversed' || m.state === 'rejected'
      const existing = matchByTxn.get(m.transaction_id)
      if (!existing) {
        matchByTxn.set(m.transaction_id, m)
        continue
      }
      const existingTerminal = existing.state === 'reversed' || existing.state === 'rejected'
      if (existingTerminal && !isTerminal) {
        matchByTxn.set(m.transaction_id, m)
      }
    }
    return rawTransactions.map((txn) => {
      const match = matchByTxn.get(txn.id)
      return match ? { ...txn, match } : txn
    })
  }, [rawTransactions, matches])

  const filteredTransactions = useMemo(() => {
    let result = transactions

    if (params.filter) {
      if (params.filter === 'no_match') {
        result = result.filter(
          (t) => !t.match || t.match.state === 'unmatched' || t.match.state === 'reversed'
        )
      } else {
        result = result.filter((t) => t.match?.state === params.filter)
      }
    }

    if (params.dateFrom) {
      result = result.filter((t) => t.date >= params.dateFrom!)
    }
    if (params.dateTo) {
      result = result.filter((t) => t.date <= params.dateTo!)
    }

    if (params.search) {
      const q = params.search.toLowerCase()
      result = result.filter(
        (t) =>
          t.counterparty.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q)
      )
    }

    return result
  }, [transactions, params.filter, params.dateFrom, params.dateTo, params.search])

  // Deep link: when navigated with ?transaction=<id>, auto-expand and scroll
  // to that row once it's been rendered. Run once per focus target so the
  // row stays user-controlled afterwards.
  useEffect(() => {
    const target = params.focusTransactionId
    if (!target) return
    if (focusHandledRef.current === target) return
    if (!filteredTransactions.some((t) => t.id === target)) return

    focusHandledRef.current = target
    if (!expandedRows.has(target)) toggleRow(target)
    // Defer scroll to next frame so the expanded panel mounts first.
    requestAnimationFrame(() => {
      const el = document.getElementById(`txn-${target}`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [params.focusTransactionId, filteredTransactions, expandedRows, toggleRow])

  // Deep-link support: when ?statement=… is set without ?account=…, resolve
  // the account from the statement row and pre-select it.
  useEffect(() => {
    if (!hasStatement || hasAccount) return
    const f = files.find((file) => file.id === params.statementId)
    if (f?.bank_account_id != null) {
      setAccountKeepStatement(f.bank_account_id)
    }
  }, [hasStatement, hasAccount, files, params.statementId, setAccountKeepStatement])

  const handleUploadComplete = (fileId: string) => {
    queryClient.invalidateQueries(['recon', 'files-detailed', params.clientId])
    setStatement(fileId)
  }

  const uploadTracker = useUploadTracker({
    clientId: params.clientId,
    bankAccountId: params.accountId,
    onUploadComplete: handleUploadComplete,
  })

  const unmatchedTotal = useMemo(() => {
    return transactions
      .filter((t) => !t.match || t.match.state === 'unmatched' || t.match.state === 'reversed')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
  }, [transactions])

  const statementCurrency = useMemo(() => {
    return transactions[0]?.currency ?? 'EUR'
  }, [transactions])

  const showEmptyState = !params.clientId || !hasAccount
  const showTransactions = hasAccount && hasStatement
  const showUploadPrompt = hasAccount && !hasStatement && !filesLoading && files.length === 0

  return (
    <div className="flex flex-col w-full gap-4">
      <h1 className="text-2xl font-bold text-foreground">Bank Reconciliation</h1>

      <ContextStrip
        clientId={params.clientId}
        onClientChange={setClient}
        files={files}
        filesLoading={filesLoading}
        onUploadClick={() => setUploadOpen(true)}
        accountId={params.accountId}
        onAccountChange={setAccount}
        statementId={params.statementId}
        onStatementChange={setStatement}
        unmatchedTotal={unmatchedTotal}
        currency={statementCurrency}
      />

      <UploadProgressStrip
        files={uploadTracker.files}
        allDone={uploadTracker.allDone}
        onDismiss={uploadTracker.dismiss}
        onReprocessDuplicate={uploadTracker.reprocessDuplicate}
        onCancelDuplicate={uploadTracker.cancelDuplicate}
      />

      {showEmptyState && (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-neutral-400">
          <Building2 className="h-12 w-12" />
          <p className="text-sm">Select a client and bank account to begin</p>
        </div>
      )}

      {showUploadPrompt && (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-neutral-400">
          <Building2 className="h-12 w-12" />
          <p className="text-sm">No statements uploaded for this client.</p>
          <button
            onClick={() => setUploadOpen(true)}
            className="text-sm font-medium text-blue-600 hover:underline"
          >
            Upload a bank statement
          </button>
        </div>
      )}

      {showTransactions && (
        <>
          <ActionStrip
            transactions={transactions}
            clientId={params.clientId!}
            statementId={params.statementId!}
            bankAccountId={params.accountId!}
            activeFilter={params.filter}
            onFilterChange={setFilter}
            hasSelectedConfirmed={filteredTransactions.some(
              (t) => selectedIds.has(t.id) && t.match?.state === 'confirmed',
            )}
          />

          <FilterBar
            dateFrom={params.dateFrom}
            dateTo={params.dateTo}
            onDateRangeChange={setDateRange}
            search={params.search ?? ''}
            onSearchChange={setSearch}
          />

          <SelectionBar
            filteredTransactions={filteredTransactions}
            selectedIds={selectedIds}
            clientId={params.clientId!}
            onClearSelection={clearSelection}
          />

          <TransactionList
            transactions={filteredTransactions}
            isLoading={txnLoading}
            clientId={params.clientId!}
            bankAccountId={params.accountId!}
            bankAccountLabel={bankAccountLabel}
            expandedRows={expandedRows}
            onToggleRow={toggleRow}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={() => {
              const visibleIds = filteredTransactions.map((t) => t.id)
              const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
              if (allSelected) {
                clearSelection()
              } else {
                setSelected(visibleIds)
              }
            }}
          />
        </>
      )}

      {params.clientId && (
        <UploadZone
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onFilesSelected={uploadTracker.uploadFiles}
        />
      )}
    </div>
  )
}
